// imports
import React, { memo } from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { TextV2, Box, Flex } from '@withjoy/joykit';

// local imports
import { useTranslation } from '@shared/core';
import { Skeleton } from '@shared/components/Skeleton';
import { IsoCurrencyIndicator } from '../../AccommodationsV2/components/JoyPlacePricing';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';

interface JoyPlacePricingMobileProps {
  accommodationPlaceId: string;
}

const PricingSkeleton = memo(() => (
  <Flex
    flexDirection="column"
    rowGap={3}
  >
    <Flex
      justifyContent="flex-end"
      alignItems="center"
      columnGap={4}
    >
      <Skeleton
        placeholder={
          <Box
            width={pxToRem(60)}
            height={pxToRem(28)}
            borderRadius={pxToRem(6)}
            backgroundColor="mono3"
          >
            &nbsp;
          </Box>
        }
      />
      <Skeleton
        placeholder={
          <Box
            width={pxToRem(30)}
            height={pxToRem(20)}
            backgroundColor="mono3"
          >
            &nbsp;
          </Box>
        }
      />
      <Skeleton
        placeholder={
          <Box
            width={pxToRem(45)}
            height={pxToRem(20)}
            backgroundColor="mono3"
          >
            &nbsp;
          </Box>
        }
      />
    </Flex>
    <Flex
      marginTop={pxToRem(10)}
      flexDirection="column"
      rowGap={2}
      textAlign="right"
    >
      <Skeleton
        placeholder={
          <Box
            width={pxToRem(30)}
            height={pxToRem(20)}
            backgroundColor="mono3"
          >
            &nbsp;
          </Box>
        }
      />
      <Skeleton
        placeholder={
          <Box
            width={pxToRem(30)}
            height={pxToRem(20)}
            backgroundColor="mono3"
          >
            &nbsp;
          </Box>
        }
      />
    </Flex>
  </Flex>
));

export const JoyPlacePricingMobile = memo<JoyPlacePricingMobileProps>(({ accommodationPlaceId }) => {
  const { t } = useTranslation('guestSiteCustom');
  const joyPlacePricingTranslation = t('joyPlacePricingMobile');
  const { price, strikeOutPrice, percentageOff, loading, totalAmount } = useTravelPlacesPricingAndBookingDetailsFragment(accommodationPlaceId, 'accommodationPlace');

  if (loading) {
    return (
      <Box marginY={pxToRem(10)}>
        <PricingSkeleton />
      </Box>
    );
  }

  if (!price) {
    return null;
  }

  return (
    <Box marginY={pxToRem(10)}>
      <Flex
        flexDirection="column"
        rowGap={3}
      >
        <Flex
          justifyContent="flex-end"
          alignItems="center"
          columnGap={4}
        >
          {percentageOff && (
            <Flex
              color="white"
              width={pxToRem(60)}
              alignItems="center"
              height={pxToRem(28)}
              justifyContent="center"
              backgroundColor="#189289"
              borderRadius={pxToRem(6)}
              typographyVariant="button1"
            >
              <TextV2 typographyVariant="label2">{joyPlacePricingTranslation.percentageOff({ percent: percentageOff })}</TextV2>
            </Flex>
          )}
          {strikeOutPrice && (
            <TextV2
              typographyVariant="label2"
              color="mono10"
              textDecoration="line-through"
            >
              <IsoCurrencyIndicator />
              {strikeOutPrice}
            </TextV2>
          )}

          <Flex
            alignItems="center"
            columnGap={2}
          >
            <TextV2
              color="mono14"
              typographyVariant="hed6"
            >
              <IsoCurrencyIndicator />
              {price}
            </TextV2>
            <TextV2
              color="mono10"
              typographyVariant="label2"
            >
              {joyPlacePricingTranslation.perNight()}
            </TextV2>
          </Flex>
        </Flex>

        {totalAmount && (
          <Flex
            marginTop={pxToRem(10)}
            flexDirection="column"
            rowGap={2}
            textAlign="right"
          >
            <TextV2
              color="mono10"
              typographyVariant="label2"
            >
              {totalAmount} {joyPlacePricingTranslation.total()}
            </TextV2>
            <TextV2
              color="mono10"
              typographyVariant="label2"
            >
              {joyPlacePricingTranslation.nightsStay()}
            </TextV2>
          </Flex>
        )}
      </Flex>
    </Box>
  );
});
